import {ReactElement, useEffect, useRef} from "react";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {restaurantSearchRedirect} from "@/utils/restaurantSearchRedirect";
import {useNavigate, useLocation} from "react-router";
import useThemePreference from "@/hooks/useThemePreference";
import {ThemePreference} from "@/types/Theme";
import useOverwolfInterop from "@/hooks/useOverwolfInterop";
import getConfig from "@/utils/getConfig";
import {AddressBooksApi} from "@devour/client";
import useNavigateWithParams from "@/hooks/useNavigateWithParams";

interface Props {
    checkOverwolf?: boolean;
}

function InitialRedirect(props: Props): ReactElement {
    const {isOnOverwolf} = useOverwolfInterop();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const lastSearchedPlaceId = useSelector((store: IStore) => store.metaStore.lastSearchedPlaceId);
    const originalTheme = useSelector((store: IStore) => store.metaStore.themePreference);
    const navigate = useNavigateWithParams();
    const location = useLocation();
    const {setTheme} = useThemePreference();
    const isRedirectingRef = useRef(false);

    useEffect(() => {
        handleRedirect();
        setTheme(ThemePreference.DARK);

        return () => {
            if (originalTheme) {
                setTheme(originalTheme);
            }
        };
    }, []);

    async function handleRedirect(): Promise<void> {
        if (isRedirectingRef.current) {
            return;
        }
        isRedirectingRef.current = true;

        // Check if we're on the root route (app startup) or restaurants route (Eat menu)
        const isAppStartup = location.pathname === "/";
        const isRestaurantRoute = location.pathname === "/restaurants";

        // App startup routing
        if (isAppStartup) {
            isRedirectingRef.current = false;
            // For Overwolf users: authenticated -> /play, non-authenticated -> /devourplay-discover
            if (isOnOverwolf) {
                if (fullToken) {
                    navigate("/play");
                } else {
                    navigate("/devourplay-discover");
                }
            } else {
                // Regular web users follow standard routing
                if (fullToken) {
                    navigate("/play");
                } else {
                    navigate("/devourplay-discover");
                }
            }
            return;
        }

        // Restaurant route (from Eat menu) - use existing logic
        if (isRestaurantRoute) {
            if (fullToken) {
                try {
                    const addressBooks = await new AddressBooksApi(getConfig(fullToken)).getAddressBooks();
                    const redirectRoute = await restaurantSearchRedirect(addressBooks, lastSearchedPlaceId);
                    if (redirectRoute) {
                        isRedirectingRef.current = false;
                        navigate(redirectRoute);
                        return;
                    }
                } catch { /* empty */ }
            }
            // Fallback for restaurant route goes to /home
            isRedirectingRef.current = false;
            navigate("/home");
            return;
        }

        // Legacy Overwolf check (kept for compatibility)
        if (isOnOverwolf && props.checkOverwolf) {
            isRedirectingRef.current = false;
            navigate("/devourplay-discover");
            return;
        }

        // Default fallback
        isRedirectingRef.current = false;
        navigate("/devourplay-discover");
    }

    // to prevent flickering while we check the location status
    return (
        <div className="landing_loading"/>
    );

}

export default InitialRedirect;
